import React, { MouseEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import logo from "../assets/images/logo_sb-blue.25.svg";
import "./FooterModern.css";

function FooterModern() {
  const navigate = useNavigate();
  const { t } = useTranslation("navigation");

  const handleCookiePreferences = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    localStorage.removeItem("cookieConsent");
    window.dispatchEvent(new CustomEvent("showCookiePreferences"));
  };

  return (
    <footer className="footer-modern">
      <div className="footer-modern__container">
        {/* Main Footer Content */}
        <div className="footer-modern__content">
          {/* Company Info */}
          <div className="footer-modern__section">
            <h3 className="footer-modern__heading">
              {t("footer.company.heading")}
            </h3>
            <nav className="footer-modern__links">
              <a
                href="/about"
                className="footer-modern__link"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/about");
                }}
              >
                {t("footer.company.about")}
              </a>
              <a
                href="/contact"
                className="footer-modern__link"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/contact");
                }}
              >
                {t("footer.company.contact")}
              </a>
            </nav>
            <div className="footer-modern__company-info">
              <p className="footer-modern__company-name">
                {t("footer.company.name")}
              </p>
              <p className="footer-modern__company-detail">
                {t("footer.company.registered")}
              </p>
              <p className="footer-modern__company-detail">
                {t("footer.company.address")
                  .split("\n")
                  .map((line, i) => (
                    <React.Fragment key={i}>
                      {line}
                      {i === 0 && <br />}
                    </React.Fragment>
                  ))}
              </p>
            </div>
          </div>

          {/* Product Links */}
          <div className="footer-modern__section">
            <h3 className="footer-modern__heading">
              {t("footer.product.heading")}
            </h3>
            <nav className="footer-modern__links">
              <a
                href="/skill-packages"
                className="footer-modern__link"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/skill-packages");
                }}
              >
                {t("footer.product.skillPackages")}
              </a>
              <a
                href="/frameworks"
                className="footer-modern__link"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/frameworks");
                }}
              >
                {t("footer.product.frameworks")}
              </a>
              <a
                href="/app/certifications"
                className="footer-modern__link"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/app/certifications");
                }}
              >
                {t("footer.product.certifications")}
              </a>
              <a
                href="/settings/subscription"
                className="footer-modern__link"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/settings/subscription");
                }}
              >
                {t("footer.product.pricing")}
              </a>
            </nav>
          </div>

          {/* Resources */}
          <div className="footer-modern__section">
            <h3 className="footer-modern__heading">
              {t("footer.resources.heading")}
            </h3>
            <nav className="footer-modern__links">
              <a
                href="/help"
                className="footer-modern__link"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/help");
                }}
              >
                {t("footer.resources.helpCenter")}
              </a>
              <a
                href="/terms"
                className="footer-modern__link"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/terms");
                }}
              >
                {t("footer.resources.terms")}
              </a>
              <a
                href="/privacy"
                className="footer-modern__link"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/privacy");
                }}
              >
                {t("footer.resources.privacy")}
              </a>
              <a
                href="/developer"
                className="footer-modern__link"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/developer");
                }}
              >
                Developers
              </a>
            </nav>
          </div>

          {/* Support */}
          <div className="footer-modern__section">
            <h3 className="footer-modern__heading">
              {t("footer.support.heading")}
            </h3>
            <nav className="footer-modern__links">
              <a
                href="/contact"
                className="footer-modern__link"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/contact");
                }}
              >
                {t("footer.support.contactUs")}
              </a>
              <a
                href={`mailto:${t("footer.support.email")}`}
                className="footer-modern__link"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("footer.support.email")}
              </a>
            </nav>
          </div>

          {/* Logo & Social */}
          <div className="footer-modern__section footer-modern__section--center">
            <button
              className="footer-modern__logo"
              onClick={() => navigate("/")}
              aria-label={t("footer.aria.homepage")}
            >
              <img src={logo} alt={t("footer.aria.logo")} />
            </button>

            <h3 className="footer-modern__heading">
              {t("footer.connect.heading")}
            </h3>
            <div className="footer-modern__social">
              <a
                href="https://www.facebook.com/Skill-Bubble-112238457197609"
                target="_blank"
                rel="noopener noreferrer"
                className="footer-modern__social-link"
                aria-label={t("footer.connect.facebook")}
              >
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
              </a>
              <a
                href="https://www.instagram.com/skillbubble/"
                target="_blank"
                rel="noopener noreferrer"
                className="footer-modern__social-link"
                aria-label={t("footer.connect.instagram")}
              >
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                </svg>
              </a>
              <a
                href="https://www.linkedin.com/company/skill-bubble-limited"
                target="_blank"
                rel="noopener noreferrer"
                className="footer-modern__social-link"
                aria-label={t("footer.connect.linkedin")}
              >
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
              </a>
            </div>

            {/* Payment Methods */}
            <div className="footer-modern__payment">
              <span className="footer-modern__payment-text">
                {t("footer.payment.secureBy")}
              </span>
              <svg
                className="footer-modern__stripe-logo"
                viewBox="0 0 60 25"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M59.64 14.28h-8.06c.19 1.93 1.6 2.55 3.2 2.55 1.64 0 2.96-.37 4.05-.95v3.32a8.33 8.33 0 0 1-4.56 1.1c-4.01 0-6.83-2.5-6.83-7.48 0-4.19 2.39-7.52 6.3-7.52 3.92 0 5.96 3.28 5.96 7.5 0 .4-.04 1.26-.06 1.48zm-5.92-5.62c-1.03 0-2.17.73-2.17 2.58h4.25c0-1.85-1.07-2.58-2.08-2.58zM40.95 20.3c-1.44 0-2.32-.6-2.9-1.04l-.02 4.63-4.12.87V5.57h3.76l.08 1.02a4.7 4.7 0 0 1 3.23-1.29c2.9 0 5.62 2.6 5.62 7.4 0 5.23-2.7 7.6-5.65 7.6zM40 8.95c-.95 0-1.54.34-1.97.81l.02 6.12c.4.44.98.78 1.95.78 1.52 0 2.54-1.65 2.54-3.87 0-2.15-1.04-3.84-2.54-3.84zM28.24 5.57h4.13v14.44h-4.13V5.57zm0-4.7L32.37 0v3.36l-4.13.88V.88zm-4.32 9.35v9.79H19.8V5.57h3.7l.12 1.22c1-1.77 3.07-1.41 3.62-1.22v3.79c-.52-.17-2.29-.43-3.32.86zm-8.55 4.72c0 2.43 2.6 1.68 3.12 1.46v3.36c-.55.3-1.54.54-2.89.54a4.15 4.15 0 0 1-4.27-4.24l.01-13.17 4.02-.86v3.54h3.14V9.1h-3.13v5.85zm-4.91.7c0 2.97-2.31 4.66-5.73 4.66a11.2 11.2 0 0 1-4.46-.93v-3.93c1.38.75 3.1 1.31 4.46 1.31.92 0 1.53-.24 1.53-1C6.26 13.77 0 14.51 0 9.95 0 7.04 2.28 5.3 5.62 5.3c1.36 0 2.72.2 4.09.75v3.88a9.23 9.23 0 0 0-4.1-1.06c-.86 0-1.44.25-1.44.9 0 1.85 6.29.97 6.29 5.88z"
                  fill="#635BFF"
                />
              </svg>
              <div className="footer-modern__payment-methods">
                {/* Visa */}
                <svg
                  className="footer-modern__payment-icon"
                  viewBox="0 0 48 32"
                  fill="none"
                >
                  <rect width="48" height="32" rx="4" fill="#1A1F71" />
                  <path
                    d="M20.5 11h-3.2l-2 12h3.2l2-12zm8.4 7.7l1.7-4.7 1 4.7h-2.7zm3.6 4.3h2.9l-2.5-12h-2.7c-.6 0-1.1.3-1.3.9l-4.6 11.1h3.4l.7-1.9h4.1v1.9zm-8.3-3.9c0-3.2-4.4-3.4-4.4-4.8 0-.4.4-.9 1.3-.9.7 0 1.5.2 2.2.5l.4-1.9c-.8-.3-1.6-.5-2.7-.5-2.8 0-4.8 1.5-4.8 3.6 0 1.6 1.4 2.4 2.5 3 1.1.5 1.5.9 1.5 1.4 0 .7-.9 1-1.7 1-.9 0-2-.2-2.9-.6l-.4 2c.9.4 2 .7 3.1.7 3 0 4.9-1.5 4.9-3.5z"
                    fill="white"
                  />
                </svg>
                {/* Mastercard */}
                <svg
                  className="footer-modern__payment-icon"
                  viewBox="0 0 48 32"
                  fill="none"
                >
                  <rect width="48" height="32" rx="4" fill="#EB001B" />
                  <circle cx="18" cy="16" r="10" fill="#FF5F00" />
                  <circle cx="30" cy="16" r="10" fill="#F79E1B" />
                  <path
                    d="M24 8.5c-1.9 1.7-3 4.2-3 7s1.1 5.3 3 7c1.9-1.7 3-4.2 3-7s-1.1-5.3-3-7z"
                    fill="#FF5F00"
                  />
                </svg>
                {/* Amex */}
                <svg
                  className="footer-modern__payment-icon"
                  viewBox="0 0 48 32"
                  fill="none"
                >
                  <rect width="48" height="32" rx="4" fill="#006FCF" />
                  <path
                    d="M15.5 13.5h-3l-.8 2h-.9l2.8-6.5h1l2.8 6.5h-1l-.9-2zm-2.6-.8h2.2l-1.1-2.6-1.1 2.6zm7.6.8h-2.2v1.2h2.1v.8h-3v-6.5h3v.8h-2.1v1.2h2.2v.8h-2.2v1.7h2.2v-.8zm3.5 2l-1.5-2.2-1.5 2.2h-1l2-3-2-3.5h1l1.5 2.2 1.5-2.2h1l-2 3.5 2 3h-1z"
                    fill="white"
                  />
                </svg>
                {/* Apple Pay */}
                <svg
                  className="footer-modern__payment-icon"
                  viewBox="0 0 48 32"
                  fill="none"
                >
                  <rect width="48" height="32" rx="4" fill="#000000" />
                  <path
                    d="M14.5 11.5c-.3.4-.8.7-1.3.6-.1-.5.1-1 .4-1.3.3-.4.8-.7 1.2-.7.1.5-.1 1-.3 1.4zm.3 1c-.7 0-1.3.4-1.6.4-.4 0-.9-.4-1.5-.4-1.2 0-2.3 1-2.3 2.8 0 1.1.4 2.3 1 3.2.5.8 1 1.4 1.7 1.4.6 0 .8-.4 1.5-.4.7 0 .9.4 1.5.4.6 0 1.1-.6 1.6-1.4.3-.5.5-1 .6-1.5-.7-.3-1.2-1-1.2-1.8 0-.7.4-1.3 1-1.6-.4-.6-1-1-1.7-1.1h-.6zm5.7-1.5v8h1.2v-2.7h1.7c1.5 0 2.6-1 2.6-2.6s-1-2.7-2.5-2.7h-2.9zm1.2 1h1.5c1 0 1.6.5 1.6 1.6s-.6 1.6-1.6 1.6h-1.5v-3.2zm5.8 7c.7 0 1.4-.4 1.7-1h.1v.9h1.1v-4.2c0-1.2-1-2-2.5-2-1.4 0-2.4.8-2.5 1.9h1.1c.1-.5.6-.9 1.3-.9.9 0 1.4.4 1.4 1.2v.5l-1.8.1c-1.7.1-2.6.8-2.6 1.9 0 1.2.9 1.9 2.2 1.9zm.3-.9c-.8 0-1.3-.4-1.3-1s.5-.9 1.4-1l1.6-.1v.5c0 .9-.7 1.5-1.7 1.5zm4.5 3.9c1.2 0 1.8-.5 2.3-1.8l2.2-6.2h-1.2l-1.5 5h-.1l-1.5-5h-1.3l2.1 5.9-.1.3c-.2.6-.5.8-1 .8h-.9v1h1z"
                    fill="white"
                  />
                </svg>
                {/* Google Pay */}
                <svg
                  className="footer-modern__payment-icon"
                  viewBox="0 0 48 32"
                  fill="none"
                >
                  <rect
                    width="48"
                    height="32"
                    rx="4"
                    fill="#FFFFFF"
                    stroke="#E8E8E8"
                  />
                  <path
                    d="M22.3 16v3.8h-1.2V11h3.2c.8 0 1.5.3 2 .8.6.5.9 1.2.9 2s-.3 1.5-.9 2c-.5.5-1.2.8-2 .8h-2zm0-4v3h2c.5 0 .9-.2 1.2-.5.3-.3.5-.7.5-1.2s-.2-.9-.5-1.2c-.3-.3-.7-.5-1.2-.5h-2zm8.9 1.5c.9 0 1.6.3 2.1.8.5.5.8 1.2.8 2.1v3.4h-1.1v-.8h-.1c-.5.6-1.1.9-1.9.9-.7 0-1.3-.2-1.8-.6-.5-.4-.7-.9-.7-1.5 0-.6.2-1.1.7-1.5.5-.4 1.1-.6 1.9-.6.7 0 1.2.1 1.7.4v-.3c0-.4-.2-.8-.5-1.1-.3-.3-.7-.5-1.2-.5-.7 0-1.2.3-1.5.8l-1-.6c.5-.8 1.3-1.2 2.4-1.2zm-1.5 5.2c0 .3.2.6.5.8.3.2.6.3 1 .3.5 0 1-.2 1.4-.6.4-.4.6-.8.6-1.3-.4-.3-.9-.5-1.6-.5-.5 0-.9.1-1.2.3-.3.2-.5.5-.5.9zm6.8-5.1h1.2l2.1 5.4h.1l2.1-5.4h1.2l-2.8 6.9c-.5 1.2-1.2 1.8-2.1 1.8h-.6v-1h.6c.5 0 .9-.3 1.1-.8l.2-.5-2.6-6.4z"
                    fill="#5F6368"
                  />
                  <path
                    d="M18.5 15.5c0-.4 0-.8-.1-1.2h-5.2v2.3h3c-.1.7-.5 1.3-1 1.7v1.5h1.6c.9-.9 1.4-2.2 1.4-3.7z"
                    fill="#4285F4"
                  />
                  <path
                    d="M13.2 20.8c1.4 0 2.5-.5 3.3-1.3l-1.6-1.2c-.4.3-1 .5-1.7.5-1.3 0-2.4-.9-2.8-2.1h-1.6v1.3c.8 1.6 2.4 2.7 4.4 2.7z"
                    fill="#34A853"
                  />
                  <path
                    d="M10.4 16.7c-.2-.5-.2-1.1 0-1.6v-1.3h-1.6c-.7 1.3-.7 2.9 0 4.2l1.6-1.3z"
                    fill="#FBBC04"
                  />
                  <path
                    d="M13.2 13.9c.7 0 1.4.3 1.9.7l1.4-1.4c-.9-.8-2-1.3-3.3-1.3-2 0-3.6 1.2-4.4 2.8l1.6 1.3c.4-1.2 1.5-2.1 2.8-2.1z"
                    fill="#EA4335"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Legal Footer */}
        <div className="footer-modern__legal">
          <div className="footer-modern__legal-links">
            <a
              href="/terms"
              className="footer-modern__legal-link"
              onClick={(e) => {
                e.preventDefault();
                navigate("/terms");
              }}
            >
              {t("footer.legal.terms")}
            </a>
            <a
              href="/privacy"
              className="footer-modern__legal-link"
              onClick={(e) => {
                e.preventDefault();
                navigate("/privacy");
              }}
            >
              {t("footer.legal.privacy")}
            </a>
            <a
              href="#"
              onClick={handleCookiePreferences}
              className="footer-modern__legal-link"
            >
              {t("footer.legal.cookiePreferences")}
            </a>
          </div>
          <div className="footer-modern__copyright">
            {t("footer.legal.copyright", { year: new Date().getFullYear() })}
          </div>
        </div>
      </div>
    </footer>
  );
}

export default FooterModern;
